import React from 'react';
import {
  Snackbar,
  IconButton,
  Collapse,
  Box,
  Typography,
  Chip,
  Paper
} from '@material-ui/core';
import { NOTIFICATION_CLOSE_ANIMATION_DELAY_MS } from '../../api/common';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CheckCircle as SuccessIcon
} from '@material-ui/icons';
import { ErrorNotification as ErrorNotificationType } from '../../types/error';

interface Props {
  notification: ErrorNotificationType;
  onClose: (id: string) => void;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
}

const ErrorNotificationComponent: React.FC<Props> = ({ 
  notification, 
  onClose, 
  position = 'top-right' 
}) => {
  const [expanded, setExpanded] = React.useState(false);
  const [open, setOpen] = React.useState(true);

  const handleClose = () => {
    setOpen(false);
    setTimeout(() => onClose(notification.id), NOTIFICATION_CLOSE_ANIMATION_DELAY_MS);
  };

  const handleExpand = () => {
    setExpanded(!expanded);
  };

  const getIcon = () => {
    switch (notification.type) {
      case 'error':
        return <ErrorIcon />;
      case 'warning':
        return <WarningIcon />;
      case 'info':
        return <InfoIcon />;
      case 'success':
        return <SuccessIcon />;
      default:
        return <InfoIcon />;
    }
  };

  const getSeverityColor = () => {
    switch (notification.type) {
      case 'error':
        return '#f44336';
      case 'warning':
        return '#ff9800';
      case 'info':
        return '#2196f3';
      case 'success':
        return '#4caf50';
      default:
        return '#2196f3';
    }
  };

  const getPosition = () => {
    const positions = {
      'top-right': { vertical: 'top' as const, horizontal: 'right' as const },
      'top-left': { vertical: 'top' as const, horizontal: 'left' as const },
      'bottom-right': { vertical: 'bottom' as const, horizontal: 'right' as const },
      'bottom-left': { vertical: 'bottom' as const, horizontal: 'left' as const },
      'top-center': { vertical: 'top' as const, horizontal: 'center' as const },
      'bottom-center': { vertical: 'bottom' as const, horizontal: 'center' as const }
    };
    return positions[position];
  };

  return (
    <Snackbar
      open={open}
      autoHideDuration={notification.autoClose ? notification.duration : null}
      onClose={handleClose}
      anchorOrigin={getPosition()}
      style={{ marginTop: 8 }}
    >
      <Paper
        elevation={3}
        style={{ 
          minWidth: 300, 
          maxWidth: 500,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          borderLeft: `4px solid ${getSeverityColor()}`,
          padding: 16
        }}
      >
        <Box display="flex" alignItems="flex-start" justifyContent="space-between">
          <Box display="flex" alignItems="flex-start" flex={1}>
            <Box mr={1} mt={0.5}>
              {getIcon()}
            </Box>
            <Box flex={1}>
              <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginBottom: 4 }}>
                {notification.title}
              </Typography>
              
              <Typography variant="body2" style={{ marginBottom: 8 }}>
                {notification.message}
              </Typography>

              {notification.details && (
                <Collapse in={expanded} timeout="auto" unmountOnExit>
                  <Box mt={1}>
                    <Typography 
                      variant="caption" 
                      color="textSecondary"
                      component="pre"
                      style={{ 
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        fontSize: '11px',
                        lineHeight: 1.4
                      }}
                    >
                      {notification.details}
                    </Typography>
                  </Box>
                </Collapse>
              )}

              <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                <Chip
                  label={notification.type.toUpperCase()}
                  size="small"
                  color={notification.type === 'error' ? 'secondary' : 'default'}
                  style={{ fontSize: '10px', height: 20 }}
                />
                
                <Typography variant="caption" color="textSecondary">
                  {notification.timestamp.toLocaleTimeString()}
                </Typography>
              </Box>
            </Box>
          </Box>
          
          <Box display="flex" alignItems="center">
            {notification.details && (
              <IconButton
                size="small"
                onClick={handleExpand}
                aria-label="expand details"
              >
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            )}
            <IconButton
              size="small"
              onClick={handleClose}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </Paper>
    </Snackbar>
  );
};

export default ErrorNotificationComponent;
